class DatePicker {
    constructor(input, opts = {}) {
        this.input = input;
        this.opts = Object.assign({
            format: 'yyyy-MM-dd',
            min: null,
            max: null,
            time: false,        // enable time picking
            time24: false,      // 24-hour mode if true, otherwise 12-hour with AM/PM
            timeStep: 1         // minute step
        }, opts);

        this.selected = null;     // Date object (date + time when time enabled)
        this.viewDate = new Date();
        this.time = { h: 0, m: 0, pm: false }; // time state for picker
        this.isOpen = false;
        this._parseInput();
        this._build();
        this._bind();
    }

    _parseInput(){
        const v = this.input.value && this.input.value.trim();
        if (!v) return;
        // support "yyyy-MM-dd" and "yyyy-MM-dd HH:mm" and ISO
        const isoDateTime = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/;
        const m = v.match(isoDateTime);
        if (m) {
            const y = parseInt(m[1],10), mo = parseInt(m[2],10)-1, d = parseInt(m[3],10);
            const hh = m[4] ? parseInt(m[4],10) : 0;
            const mm = m[5] ? parseInt(m[5],10) : 0;
            this.selected = new Date(y, mo, d, hh, mm, 0, 0);
            this.viewDate = new Date(this.selected);
            this.time.h = this.selected.getHours();
            this.time.m = this.selected.getMinutes();
            this.time.pm = this.time.h >= 12;
            return;
        }
        const parsed = new Date(v);
        if (!isNaN(parsed)) {
            this.selected = parsed;
            this.viewDate = new Date(parsed);
            this.time.h = parsed.getHours();
            this.time.m = parsed.getMinutes();
            this.time.pm = this.time.h >= 12;
        }
    }

    _build(){
        const wrap = document.createElement('div');
        wrap.className = 'simple-datepicker-wrapper';
        this.input.parentNode.insertBefore(wrap, this.input);
        wrap.appendChild(this.input);
        this.wrapper = wrap;

        const panel = document.createElement('div');
        panel.className = 'datepicker';
        panel.style.display = 'none';
        panel.innerHTML = `
          <div class="header">
            <button type="button" data-action="prev" aria-label="Previous month">&#x276E;</button>
            <div class="month-year"></div>
            <button type="button" data-action="next" aria-label="Next month">&#x276F;</button>
          </div>
          <div class="weekdays"></div>
          <div class="days"></div>
        `;
        wrap.appendChild(panel);
        this.panel = panel;

        // weekdays
        const weekdays = ['Su','Mo','Tu','We','Th','Fr','Sa'];
        const wd = panel.querySelector('.weekdays');
        weekdays.forEach(w => { const d = document.createElement('div'); d.textContent = w; wd.appendChild(d); });

        // time area (conditionally rendered)
        this.timeContainer = document.createElement('div');
        this.timeContainer.className = 'time-picker';
        this.timeContainer.style.display = this.opts.time ? '' : 'none';
        panel.appendChild(this.timeContainer);
        if (this.opts.time) this._buildTimeControls();

        // footer buttons for confirm/now if time enabled
        this.footer = document.createElement('div');
        this.footer.className = 'datepicker-footer';
        if (this.opts.time) {
            this.footer.innerHTML = `
              <button type="button" class="btn-now">Now</button>
              <button type="button" class="btn-set">Set</button>
            `;
            panel.appendChild(this.footer);
        }

        this._renderDays();
    }

    _buildTimeControls(){
        const tc = this.timeContainer;
        tc.innerHTML = `
          <div class="time-fields" style="display:flex;gap:6px;align-items:center;margin-top:8px;">
            <div class="hour-wrap">
              <button type="button" data-time="hour-dec">−</button>
              <input type="number" class="tp-hour" min="0" max="23" value="${this._pad(this._getDisplayHour())}" />
              <button type="button" data-time="hour-inc">+</button>
            </div>
            <div class="minute-wrap">
              <button type="button" data-time="min-dec">−</button>
              <input type="number" class="tp-minute" min="0" max="59" step="${this.opts.timeStep}" value="${this._pad(this.time.m)}" />
              <button type="button" data-time="min-inc">+</button>
            </div>
            <div class="ampm-wrap" style="${this.opts.time24 ? 'display:none;' : ''}">
              <button type="button" class="tp-ampm">${this.time.pm ? 'PM' : 'AM'}</button>
            </div>
          </div>
        `;
        // references
        this.hourInput = tc.querySelector('.tp-hour');
        this.minInput = tc.querySelector('.tp-minute');
        this.ampmBtn = tc.querySelector('.tp-ampm');

        // clamp initial values
        this._syncTimeInputs();
    }

    _bind(){
        // open/close
        this.input.addEventListener('focus', () => this.open());
        this.input.addEventListener('click', () => this.open());

        // nav & day click
        this.panel.addEventListener('click', (e) => {
            const btn = e.target.closest('button[data-action]');
            if (btn) {
                e.preventDefault();
                if (btn.dataset.action === 'prev') this._addMonth(-1);
                else this._addMonth(1);
                return;
            }
            // time control buttons
            const tbtn = e.target.closest('[data-time]');
            if (tbtn) {
                e.preventDefault();
                this._handleTimeButton(tbtn.dataset.time);
                return;
            }
            if (e.target.classList.contains('tp-ampm')) {
                this.time.pm = !this.time.pm;
                this._syncTimeInputs();
                return;
            }
            if (e.target.classList.contains('btn-now')) {
                this._setNow();
                return;
            }
            if (e.target.classList.contains('btn-set')) {
                this._applyTimeToSelectedAndClose();
                return;
            }

            const dayEl = e.target.closest('.day');
            if (dayEl && !dayEl.classList.contains('disabled') && !dayEl.classList.contains('empty')) {
                const y = parseInt(dayEl.dataset.y,10);
                const m = parseInt(dayEl.dataset.m,10);
                const d = parseInt(dayEl.dataset.d,10);
                const chosen = new Date(y, m, d, this.time.h, this.time.m);
                // if time enabled, select date but keep panel open for user to adjust time unless shift-click
                if (this.opts.time) {
                    this.selected = chosen;
                    this.viewDate = new Date(chosen);
                    this._renderDays();
                } else {
                    this._select(chosen);
                }
            }
        });

        // direct change on numeric inputs
        this.panel.addEventListener('change', (e) => {
            if (e.target === this.hourInput || e.target === this.minInput) {
                this._handleTimeInputChange();
            }
        });

        // keyboard navigation
        this.input.addEventListener('keydown', (e) => {
            if (!this.isOpen) {
                if (['ArrowDown','Enter'].includes(e.key)) { e.preventDefault(); this.open(); }
                return;
            }
            switch(e.key){
                case 'Escape': this.close(); break;
                case 'ArrowLeft': e.preventDefault(); this._moveSelected(-1); break;
                case 'ArrowRight': e.preventDefault(); this._moveSelected(1); break;
                case 'ArrowUp': e.preventDefault(); this._moveSelected(-7); break;
                case 'ArrowDown': e.preventDefault(); this._moveSelected(7); break;
                case 'Enter':
                    e.preventDefault();
                    if (this.opts.time) this._applyTimeToSelectedAndClose();
                    else if (this.focusedDate) this._select(new Date(this.focusedDate));
                    break;
            }
        });

        // click outside to close
        document.addEventListener('click', (e) => {
            if (!this.wrapper.contains(e.target)) {
                // if time enabled and user closes outside, apply current selected (if any)
                if (this.opts.time && this.selected) this._applyTimeToSelectedAndClose();
                else this.close();
            }
        });

        // adjust on scroll/resize
        window.addEventListener('resize', () => this._reposition());
        window.addEventListener('scroll', () => this._reposition(), true);
    }

    open(){
        this.isOpen = true;
        this.panel.style.display = 'block';
        this._renderDays();
        this._reposition();
        this.input.focus();
    }

    close(){
        this.isOpen = false;
        this.panel.style.display = 'none';
    }

    _reposition(){
        const rect = this.input.getBoundingClientRect();
        const top = rect.bottom + window.pageYOffset;
        const left = rect.left + window.pageXOffset;
        this.panel.style.position = 'absolute';
        this.panel.style.top = '40px';
        this.panel.style.left = '5px';
        /* this.panel.style.top = top + 'px';
        this.panel.style.left = left + 'px'; */
    }

    _addMonth(n){
        this.viewDate.setMonth(this.viewDate.getMonth() + n);
        this._renderDays();
    }

    _renderDays(){
        const monthYear = this.panel.querySelector('.month-year');
        const daysContainer = this.panel.querySelector('.days');
        daysContainer.innerHTML = '';

        const y = this.viewDate.getFullYear();
        const m = this.viewDate.getMonth();
        monthYear.textContent = this.viewDate.toLocaleString(undefined, { month: 'long', year: 'numeric' });

        const first = new Date(y, m, 1);
        const startOffset = first.getDay();
        const daysInMonth = new Date(y, m+1, 0).getDate();

        for (let i=0;i<startOffset;i++){
            const blank = document.createElement('div');
            blank.className = 'day empty';
            daysContainer.appendChild(blank);
        }

        const today = new Date();
        today.setHours(0,0,0,0);

        const min = this.opts.min ? this._coerceDate(this.opts.min) : null;
        const max = this.opts.max ? this._coerceDate(this.opts.max) : null;

        for (let d=1; d<=daysInMonth; d++){
            const dt = new Date(y, m, d);
            const cell = document.createElement('div');
            cell.className = 'day';
            cell.textContent = String(d);
            cell.dataset.y = y;
            cell.dataset.m = m;
            cell.dataset.d = d;

            if (dt.getTime() === today.getTime()) cell.classList.add('today');
            if (this.selected && this._isSameDate(dt, this.selected)) cell.classList.add('selected');

            if ((min && dt < min) || (max && dt > max)) cell.classList.add('disabled');
            else this.focusedDate = dt;
 
            daysContainer.appendChild(cell);
        }
    }

    _select(date){
        this.selected = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());
        this.input.value = this._formatDate(this.selected);
        console.log("selected date:" + this._formatDate(this.selected));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
    }

    _applyTimeToSelectedAndClose(){
        if (!this.selected) {
            // if no date selected yet, use viewDate's first day
            this.selected = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), this.viewDate.getDate(), this.time.h, this.time.m);
        } else {
            this.selected.setHours(this.time.h, this.time.m, 0, 0);
        }
        this.input.value = this._formatDate(this.selected);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
    }

    _handleTimeButton(action){
        const step = Math.max(1, parseInt(this.opts.timeStep,10) || 1);
        if (action === 'hour-inc') this.time.h = (this.time.h + 1) % 24;
        if (action === 'hour-dec') this.time.h = (this.time.h + 23) % 24;
        if (action === 'min-inc') this.time.m = (this.time.m + step) % 60;
        if (action === 'min-dec') this.time.m = (this.time.m + 60 - step) % 60;
        this._syncTimeInputs();
    }

    _handleTimeInputChange(){
        let h = parseInt(this.hourInput.value,10);
        let m = parseInt(this.minInput.value,10);
        if (isNaN(h)) h = 0;
        if (isNaN(m)) m = 0;
        // clamp
        h = Math.max(0, Math.min(23, h));
        m = Math.max(0, Math.min(59, m));
        this.time.h = h;
        this.time.m = m;
        this._syncTimeInputs();
    }

    _setNow(){
        const now = new Date();
        this.time.h = now.getHours();
        this.time.m = now.getMinutes();
        this.time.pm = this.time.h >= 12;
        this._syncTimeInputs();
        // if there's already a selected date, update its time; otherwise set selected to today
        if (!this.selected) this.selected = new Date(now.getFullYear(), now.getMonth(), now.getDate(), this.time.h, this.time.m);
        else this.selected.setHours(this.time.h, this.time.m, 0, 0);
        this._renderDays();
    }

    _syncTimeInputs(){
        if (!this.hourInput || !this.minInput) return;
        if (this.opts.time24) {
            this.hourInput.value = this._pad(this.time.h);
        } else {
            const displayH = this._getDisplayHour();
            this.hourInput.value = this._pad(displayH);
            if (this.ampmBtn) this.ampmBtn.textContent = this.time.pm ? 'PM' : 'AM';
        }
        this.minInput.value = this._pad(this.time.m);
    }

    _getDisplayHour(){
        if (this.opts.time24) return this.time.h;
        const h = this.time.h % 12;
        return h === 0 ? 12 : h;
    }

    _formatDate(date){
        if (!date) return '';
        const z = (n) => n < 10 ? '0'+n : String(n);
        const hh24 = date.getHours();
        const hh12 = (() => { const h = hh24 % 12; return h === 0 ? 12 : h; })();
        const mm = date.getMinutes();
        
        // Get full month name
        const monthName = date.toLocaleString('en-US', { month: 'long' });
        
        let result = this.opts.format
            .replace('yyyy', date.getFullYear())
            .replace('MMMM', monthName)  // full month name (January, February, etc.)
            .replace('MM', z(date.getMonth()+1))  // numeric month
            .replace('dd', z(date.getDate()));
        
        if (this.opts.time) {
            if (this.opts.time24) {
                result += ` ${z(hh24)}:${z(mm)}`;
            } else {
                const ampm = hh24 >= 12 ? 'PM' : 'AM';
                result += ` ${z(hh12)}:${z(mm)} ${ampm}`;
            }
        }
        return result;
    }

    _parseDateString(s){
        const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
        const m = s.match(iso);
        if (m) return new Date(parseInt(m[1],10), parseInt(m[2],10)-1, parseInt(m[3],10));
        const parsed = new Date(s);
        if (!isNaN(parsed)) return parsed;
        return null;
    }

    _isSameDate(a,b){ return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }

    _coerceDate(v){
        if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
        return this._parseDateString(String(v));
    }

    _moveSelected(offsetDays){
        let base = this.selected ? new Date(this.selected) : (this.focusedDate ? new Date(this.focusedDate) : new Date());
        base.setDate(base.getDate() + offsetDays);
        if (base.getMonth() !== this.viewDate.getMonth() || base.getFullYear() !== this.viewDate.getFullYear()) {
            this.viewDate = new Date(base.getFullYear(), base.getMonth(), 1);
            this._renderDays();
        }
        const sel = this.panel.querySelector(`.day[data-y="${base.getFullYear()}"][data-m="${base.getMonth()}"][data-d="${base.getDate()}"]`);
        if (sel && !sel.classList.contains('disabled') && !sel.classList.contains('empty')) {
            if (this.opts.time) {
                this.selected = new Date(base.getFullYear(), base.getMonth(), base.getDate(), this.time.h, this.time.m);
                this._renderDays();
            } else {
                this._select(base);
            }
        } else {
            this.focusedDate = base;
        }
    }

    _pad(n){ return n < 10 ? '0' + n : String(n); }
}

const datePickerInit = () => {
    document.querySelectorAll('.simple-datepicker-input').forEach(function(el){
        const dateTime = el.getAttribute('data-datetime');
        let timeSet = false;
        if (dateTime == "datetime" || dateTime == "time"){
            timeSet = true;
        }
        new DatePicker(el, { format: 'MMMM dd, yyyy', time: timeSet });
    });
};

if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded', datePickerInit);
} else {
    datePickerInit();
};